import { useForm } from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import {
  FaFileAlt,
  FaUpload,
  FaTimes,
  FaCheck,
  FaDownload,
} from 'react-icons/fa'
import { createToast } from '../../utils/Toast'
import axios from 'axios'
import { BeatLoader } from 'react-spinners'
import { fetchAuthSession } from 'aws-amplify/auth'
import Button from '../../components/atoms/Buttons'

const Toast = createToast()

export default function UploadCatalog({ manufacturer }) {
  const [selectedFiles, setSelectedFiles] = useState({
    Catalogfile: null,
    Measurementsfile: null,
  })
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [editingFile, setEditingFile] = useState(null)
  const [currentFiles, setCurrentFiles] = useState({
    catalog: null,
    measurements: null,
  })
  const [isLoading, setIsLoading] = useState(true)

  const fetchFilesMetadata = async type => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_FILE_API_URL}?brand=${manufacturer.manufacturerObject?.brand
        }&type=${type}&responseType=metadata`,
      )
      return response.data
    } catch (error) {
      console.error(`Error fetching ${type} metadata:`, error)
      return null
    }
  }

  useEffect(() => {
    const loadFiles = async () => {
      setIsLoading(true)
      try {
        const [catalogData, measurementsData] = await Promise.all([
          fetchFilesMetadata('catalog'),
          fetchFilesMetadata('measurements'),
        ])

        setCurrentFiles({
          catalog: catalogData,
          measurements: measurementsData,
        })
      } catch (error) {
        console.error('Error loading files:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (manufacturer.manufacturerObject?.brand) {
      loadFiles()
    } else {
      setIsLoading(false)
    }
  }, [manufacturer.manufacturerObject?.brand])

  const formSchema = z.object({
    Catalogfile: z
      .any()
      .optional()
      .refine(
        file => {
          if (!file || !(file instanceof File)) return true
          return file.type === 'application/json'
        },
        {
          message: 'File must be a json file',
        },
      )
      .refine(
        file => {
          if (!file || !(file instanceof File)) return true
          return file.size <= 5 * 1024 * 1024
        },
        {
          message: 'File size must be less than 5MB',
        },
      ),
    Measurementsfile: z
      .any()
      .optional()
      .refine(
        file => {
          if (!file || !(file instanceof File)) return true
          return file.type === 'application/json'
        },
        {
          message: 'File must be a json file',
        },
      )
      .refine(
        file => {
          if (!file || !(file instanceof File)) return true
          return file.size <= 5 * 1024 * 1024
        },
        {
          message: 'File size must be less than 5MB',
        },
      ),
  })

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
  })

  const handleFileChange = (fieldName, event) => {
    const file = event.target.files[0]
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [fieldName]: file,
      }))
      setValue(fieldName, file)
    }
  }

  const handleDelete = fieldName => {
    setSelectedFiles(prev => ({
      ...prev,
      [fieldName]: null,
    }))
    setValue(fieldName, null)

    const fileInput = document.querySelector(`input[name="${fieldName}"]`)
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const startEditing = fileType => {
    setEditingFile(fileType)
    setSelectedFiles(prev => ({
      ...prev,
      [fileType === 'catalog' ? 'Catalogfile' : 'Measurementsfile']: null,
    }))
    reset()
  }

  const cancelEditing = () => {
    setEditingFile(null)
    setSelectedFiles({
      Catalogfile: null,
      Measurementsfile: null,
    })
    reset()
  }

  const getSignedUrl = async (fileName, fileType) => {
    try {
      const { tokens } = await fetchAuthSession()
      const idToken = tokens.idToken?.toString()
      const response = await fetch(
        `${import.meta.env.VITE_FILE_API_URL}/put-signed-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            type: fileType,
            brand: manufacturer.manufacturerObject?.brand || 'mycactus',
            fileName: fileName,
            fileType: 'json',
          }),
        },
      )

      if (!response.ok) {
        throw new Error('Failed to get signed URL')
      }

      const data = await response.json()
      return data.signedUrl
    } catch (error) {
      console.error('Error getting signed URL:', error)
      throw error
    }
  }

  const uploadFileToSignedUrl = async (file, signedUrl, fieldName) => {
    try {
      const response = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to upload ${fieldName}`)
      }

      return response.url.split('?')[0]
    } catch (error) {
      console.error(`Error uploading ${fieldName}:`, error)
      throw error
    }
  }

  const downloadFile = async fileType => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_FILE_API_URL}?brand=${manufacturer.manufacturerObject?.brand
        }&type=${fileType}&responseType=file`,
      )

      // Get the signed URL from the response
      const signedUrl = response.data.url

      // Fetch the file from the signed URL as a blob
      const fileResponse = await fetch(signedUrl)
      const blob = await fileResponse.blob()

      // Create a blob URL for download
      const url = window.URL.createObjectURL(blob)

      // Create download link
      const a = document.createElement('a')
      a.href = url
      a.download = `${manufacturer.manufacturerObject?.brand
        }-${fileType} on ${new Date().toLocaleDateString()}.json`
      document.body.appendChild(a)
      a.click()

      // Clean up
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
      Toast.error('Failed to download file')
    }
  }

  const onSubmit = async data => {
    setUploading(true)
    setUploadProgress({})

    try {
      const uploadResults = {}

      if (data.Catalogfile) {
        setUploadProgress(prev => ({
          ...prev,
          catalog: 'Getting signed URL...',
        }))
        const catalogSignedUrl = await getSignedUrl(
          `catalog-${manufacturer.id}-${Date.now()}`,
          'catalog',
        )

        setUploadProgress(prev => ({
          ...prev,
          catalog: 'Uploading catalog file...',
        }))
        const catalogUrl = await uploadFileToSignedUrl(
          data.Catalogfile,
          catalogSignedUrl,
          'catalog',
        )
        uploadResults.catalogUrl = catalogUrl

        const catalogMetadata = await fetchFilesMetadata('catalog')

        if (!manufacturer.manufacturerObject?.isShopifyConnected) {
          const syncFile = async () => {
            const response = await axios.post(
              `${import.meta.env.VITE_FILE_SYNC_URL}`,
              {
                key: catalogMetadata.Key,
              },
            )
            return response
          }
          const syncResponse = await syncFile()
          console.log('Sync response:', syncResponse)

          setTimeout(async () => {
            if (syncResponse.status === 200) {
              Toast.fire({
                icon: 'success',
                title:
                  syncResponse.data.message ||
                  'Catalog file synced successfully',
              })
            } else {
              Toast.fire({
                icon: 'error',
                title:
                  syncResponse.data.message || 'Failed to sync catalog file',
              })
            }
          }, 1000)
        }

        // Update current files state
        setCurrentFiles(prev => ({
          ...prev,
          catalog: {
            name: data.Catalogfile.name,
            url: catalogUrl,
            lastUpdated: new Date().toISOString(),
            size: data.Catalogfile.size,
          },
        }))
      }

      if (data.Measurementsfile) {
        setUploadProgress(prev => ({
          ...prev,
          measurements: 'Getting signed URL...',
        }))
        const measurementsSignedUrl = await getSignedUrl(
          `measurements-${manufacturer.id}-${Date.now()}`,
          'measurements',
        )

        setUploadProgress(prev => ({
          ...prev,
          measurements: 'Uploading measurements file...',
        }))
        const measurementsUrl = await uploadFileToSignedUrl(
          data.Measurementsfile,
          measurementsSignedUrl,
          'measurements',
        )
        uploadResults.measurementsUrl = measurementsUrl

        const measurementsMetadata = await fetchFilesMetadata('measurements')

        if (!manufacturer.manufacturerObject?.isShopifyConnected) {
          const syncFile = async () => {
            const response = await axios.post(
              `${import.meta.env.VITE_FILE_SYNC_URL}`,
              {
                key: measurementsMetadata.Key,
              },
            )
            return response
          }
          const syncResponse = await syncFile()

          setTimeout(() => {
            if (syncResponse.status === 200) {
              Toast.fire({
                icon: 'success',
                title:
                  syncResponse.data.message ||
                  'Measurements file synced successfully',
              })
            } else {
              Toast.fire({
                icon: 'error',
                title:
                  syncResponse.data.message ||
                  'Failed to sync measurements file',
              })
            }
          }, 1000)
        }
        // Update current files state
        setCurrentFiles(prev => ({
          ...prev,
          measurements: {
            name: data.Measurementsfile.name,
            url: measurementsUrl,
            lastUpdated: new Date().toISOString(),
            size: data.Measurementsfile.size,
          },
        }))
      }

      console.log('Upload successful:', uploadResults)

      // Reset form and exit edit mode
      setSelectedFiles({
        Catalogfile: null,
        Measurementsfile: null,
      })
      setValue('Catalogfile', null)
      setValue('Measurementsfile', null)
      setEditingFile(null)
      reset()

      document.querySelectorAll('input[type="file"]').forEach(input => {
        input.value = ''
      })

      Toast.fire({
        icon: 'success',
        title: 'Files uploaded successfully!',
      })
    } catch (error) {
      console.error('Upload failed:', error)
      Toast.fire({
        icon: 'error',
        title: 'Upload failed: ' + error.message,
      })
    } finally {
      setUploading(false)
      setUploadProgress({})
    }
  }

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const parseFileName = fileName => {
    if (!fileName) return { displayName: 'Unknown file', uploadDate: null }
    const parts = fileName.split('-')
    if (parts.length >= 2) {
      const lastTimestamp = parts[parts.length - 1].replace('.json', '')
      if (
        (lastTimestamp.length === 10 || lastTimestamp.length === 13) &&
        !isNaN(parseInt(lastTimestamp))
      ) {
        const timestampMs =
          lastTimestamp.length === 10
            ? parseInt(lastTimestamp) * 1000
            : parseInt(lastTimestamp)
        const uploadDate = new Date(timestampMs)
        const displayName = `${parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
          } uploaded on ${formatDate(uploadDate)}`
        return { displayName, uploadDate }
      }
    }
    return { displayName: fileName, uploadDate: null }
  }

  const renderCurrentFile = (fileType, fileData) => {
    const mainFileId =
      fileData?.fileName || fileData?.name || fileData?.Key || ''
    const noFile = !mainFileId || mainFileId === 'Unknown file'
    const isEditing = editingFile === fileType || noFile
    const fieldName =
      fileType === 'catalog' ? 'Catalogfile' : 'Measurementsfile'
    return (
      <div className="border rounded-lg p-2 mb-4 font-montserrat">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800 capitalize">
            {noFile
              ? `Upload New ${fileType.charAt(0).toUpperCase() + fileType.slice(1)
              } File`
              : `${fileType.charAt(0).toUpperCase() + fileType.slice(1)} File`}
          </h3>
          <div className="flex gap-2">
            {!isEditing && !isLoading && (
              <Button
                type="button"
                onClick={() => startEditing(fileType)}
                AppendIcon={FaUpload}
                primary
                className="create-saddle-btn !w-auto px-10 cursor-pointer"
                disabled={uploading}
              >
                Change
              </Button>
            )}
            {selectedFiles[fieldName] && (
              <Button
                type="button"
                onClick={() => handleSubmit(onSubmit)()}
                AppendIcon={FaCheck}
                primary
                disabled={uploading}
                className="create-saddle-btn !w-auto px-6 cursor-pointer"
              >
                {uploading ? 'Uploading...' : 'Save Changes'}
              </Button>
            )}
            {editingFile === fileType && (
              <Button
                type="button"
                onClick={cancelEditing}
                primaryOutline
                className="create-saddle-btn !w-auto px-6 cursor-pointer"
                disabled={uploading}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="space-y-3 font-montserrat">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload New{' '}
                {fileType?.charAt(0).toUpperCase() + fileType?.slice(1)} File
              </label>
              <input
                name={fieldName}
                type="file"
                accept=".json"
                onChange={e => handleFileChange(fieldName, e)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={uploading}
              />
              {selectedFiles[fieldName] && (
                <div className="flex items-center gap-2 mt-2">
                  <p className="text-sm text-gray-600">
                    Selected: {selectedFiles[fieldName]?.name}
                  </p>
                  <button
                    onClick={() => handleDelete(fieldName)}
                    type="button"
                    className="text-sm text-red-600 hover:text-red-800"
                    disabled={uploading}
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              )}
              {uploadProgress[fileType] && (
                <p className="text-sm text-blue-600 mt-1">
                  {uploadProgress[fileType]}
                </p>
              )}
              {errors[fieldName] && (
                <p className="text-red-500 text-sm mt-1">
                  {errors[fieldName].message}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer font-montserrat">
            <FaFileAlt className="text-blue-500" size={24} />
            <div className="flex-1">
              {isLoading ? (
                <div className="flex items-center h-full">
                  <BeatLoader color={'#2b364b'} />
                </div>
              ) : fileData ? (
                <>
                  <p className="font-medium text-gray-800">
                    {
                      parseFileName(
                        fileData.fileName || fileData.name || fileData.Key,
                      ).displayName
                    }
                  </p>
                  <p className="text-sm text-gray-600">
                    File size:{' '}
                    {Math.round((fileData.Size || fileData.size) / 1024)} KB
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500">
                  No {fileType} file uploaded yet
                </p>
              )}
            </div>
            <div
              onClick={() => downloadFile(fileType)}
              className="cursor-pointer"
            >
              <FaDownload size={20} />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg p-2 font-montserrat">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Current Files
        </h2>
        <p className="text-gray-600">
          View and update your catalog and measurements files. Click "Change" to
          upload a new version.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {!manufacturer.manufacturerObject?.isShopifyConnected &&
          renderCurrentFile('catalog', currentFiles.catalog)}
        {renderCurrentFile('measurements', currentFiles.measurements)}
      </form>
    </div>
  )
}
